import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import ApiCallServices from "../../../../services/ApiCallServices";
import { END_POINTS } from '../../../../services/endpoint/endPoint';
import button from "../../../button.module.css";
import "../../dashboard/overview/overview.css";

const SiteExpansion = () => {
    const [data, setData] = useState([]);
    const [publishers, setPublishers] = useState([]);

    const [publisherFilter, setPublisherFilter] = useState('');

    const [publisherName, setPublisherName] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [sisterDomains, setSisterDomains] = useState([]);
    const [dbDomains, setDbDomains] = useState([]);
    const [showSisterList, setShowSisterList] = useState(false);

    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('publisher_name');
    const [sortOrder, setSortOrder] = useState('ASC');
    const [totalPages, setTotalPages] = useState(1);

    // Site/app names + publisher data from the DB
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page,
                limit,
                search,
                sortBy,
                sortOrder,
            });

            const res = await ApiCallServices.getlisting(`${END_POINTS.publishersList}?${params}`);

            if (res.status === 200) {
                const apiData = res.data.data.data;
                setData(apiData);

                const unique = {};
                apiData.forEach(item => {
                    if (!unique[item.publisher_id]) {
                        unique[item.publisher_id] = item.publisher_name;
                    }
                });

                setPublishers(Object.entries(unique).map(([id, name]) => ({
                    id,
                    name
                })));

                setTotalPages(res.data.data.pagination.totalPages);
            }
        } catch {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [page, limit, search, sortBy, sortOrder]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const filteredData = useMemo(() => {
        if (!publisherFilter) return data;
        return data.filter(item => item.publisher_id.toString() === publisherFilter);
    }, [data, publisherFilter]);

    const handleSort = (column) => {
        setPage(1);

        if (sortBy === column) {
            setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(column);
            setSortOrder('ASC');
        }
    };

    const getPagination = () => {
        let pages = [];
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + 4);

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    // ChatGPT-backed sister domain lookup
    const handleAddSisterDomains = async () => {
        if (!publisherName.trim()) {
            toast.error('Please enter a site/app name');
            return;
        }

        setLoading(true);
        try {
            const params = new URLSearchParams({ search: publisherName.trim() });

            const [chatGptRes, dbRes] = await Promise.all([
                ApiCallServices.create(
                    `${END_POINTS.addSisterDomains}?${params}`,
                    { publisher_name: publisherName.trim() }
                ),
                ApiCallServices.getlisting(
                    `${END_POINTS.publishersList}?page=1&limit=1000&search=${encodeURIComponent(publisherName.trim())}&sortBy=publisher_name&sortOrder=ASC`
                ),
            ]);

            // DB results come first — dedupe, cap at 20
            let dbAllSet = new Set();
            if (dbRes.status === 200) {
                const seen = new Set();
                const dbAll = dbRes.data.data.data
                    .map((item) => item.site_name)
                    .filter((name) => {
                        const key = name.trim().toLowerCase();
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                    });
                dbAllSet = new Set(dbAll.map((d) => d.trim().toLowerCase()));
                setDbDomains(dbAll.slice(0, 20));
            }

            // ChatGPT results second — dedupe, exclude anything already in DB, cap at 20
            if (chatGptRes.status === 200) {
                const seen = new Set();
                const gptDomains = (chatGptRes.data.message || []).filter((d) => {
                    const key = d.trim().toLowerCase();
                    if (seen.has(key) || dbAllSet.has(key)) return false;
                    seen.add(key);
                    return true;
                });
                setSisterDomains(gptDomains.slice(0, 20));
                setShowSisterList(true);
                fetchData();
                toast.success('Sister domains retrieved');
            }
        } catch {
            toast.error('Failed to get sister domains');
        } finally {
            setLoading(false);
        }
    };

    const handleShowModal = () => {
        setShowModal(true);
        document.body.style = 'overflow: hidden';
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setPublisherName('');
        setSisterDomains([]);
        setDbDomains([]);
        setShowSisterList(false);
        document.body.removeAttribute('style');
    };

    const handleDownloadSisterDomains = () => {
        if (sisterDomains.length === 0) {
            toast.error('No sister domains to download');
            return;
        }

        const csvContent = [
            ['Sister Domains for: ' + publisherName],
            [''],
            ['S.No', 'Domain'],
            ...sisterDomains.map((domain, index) => [index + 1, domain])
        ]
            .map(row => row.join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `sister-domains-${publisherName.replace(/\s+/g, '-')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Sister domains downloaded successfully');
    };

    return (
        <>
            <ToastContainer />

            <div className="container-fluid">

                <div className='filter-wraper justify-content-between'>
                    <div className='d-flex align-items-center fields'>
                        <h3>Site Expansion</h3>
                    </div>
                </div>

                <div className="filter-wraper d-flex justify-content-between">

                    <div className="d-flex align-items-center fields">
                        <div className="form-group search-icon">
                            <input
                                type="text"
                                className="form-control"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                                placeholder="Search publisher or site/app name"
                            />
                        </div>
                    </div>

                    <div className="form-group d-flex justify-content-end">
                        <button
                            type="button"
                            className={`${button.btn} ${button.btnBlue}`}
                            onClick={handleShowModal}
                        >
                            Get Sister Domains
                        </button>
                    </div>
                </div>

                <div className="table-responsive custom-table">
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Sr. No.</th>
                                <th onClick={() => handleSort('publisher_name')}>
                                    Publisher <i className="fas fa-sort"></i>
                                </th>
                                <th onClick={() => handleSort('site_name')}>
                                    Site/App Name <i className="fas fa-sort"></i>
                                </th>
                                <th>Type</th>
                                <th>Status</th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading ? (
                                <tr><td colSpan="5" className="text-center">Loading...</td></tr>
                            ) : filteredData.length === 0 ? (
                                <tr><td colSpan="5" className="text-center">No Data</td></tr>
                            ) : (
                                filteredData.map((item, index) => (
                                    <tr key={`${item.publisher_id}-${item.site_id}`}>
                                        <td>{(page - 1) * limit + index + 1}</td>
                                        <td>{item.publisher_name}</td>
                                        <td>{item.site_name}</td>
                                        <td>{item.site_app}</td>
                                        <td>{item.site_status ? 'Active' : 'Inactive'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">

                    <div>
                        <select
                            className="form-control"
                            value={limit}
                            onChange={(e) => {
                                setLimit(parseInt(e.target.value));
                                setPage(1);
                            }}
                        >
                            <option value={20}>20 per page</option>
                            <option value={50}>50 per page</option>
                            <option value={100}>100 per page</option>
                        </select>
                    </div>

                    <ul className="pagination">
                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(page - 1)}>
                                Previous
                            </button>
                        </li>

                        {getPagination().map(p => (
                            <li key={p} className={`page-item ${page === p ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => setPage(p)}>
                                    {p}
                                </button>
                            </li>
                        ))}

                        <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPage(page + 1)}>
                                Next
                            </button>
                        </li>
                    </ul>
                </div>
            </div>

            <div className={`${showModal ? "overlay" : ""}`}></div>

            {showModal && (
                <div className={`modal form-modal show`}>
                    <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: '80vw', height: '80vh' }}>
                        <div className="modal-content" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {!showSisterList ? 'Get Sister Domains' : `Sister Domains for ${publisherName}`}
                                </h5>
                                <i className="fa-regular fa-circle-xmark close-icon" onClick={handleCloseModal}></i>
                            </div>

                            <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                                {!showSisterList ? (
                                    <div className="form-group">
                                        <label>Site/App Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={publisherName}
                                            onChange={(e) => setPublisherName(e.target.value)}
                                            placeholder="Enter site or app name"
                                        />
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                            gap: '10px'
                                        }}>
                                            {dbDomains.map((d, i) => (
                                                <div
                                                    key={`db-${i}`}
                                                    style={{
                                                        padding: '10px',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px',
                                                        backgroundColor: '#eef6ff',
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    <small className="text-muted">#{i + 1}</small>
                                                    <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>{d}</p>
                                                </div>
                                            ))}
                                            {sisterDomains.map((d, i) => (
                                                <div
                                                    key={`gpt-${i}`}
                                                    style={{
                                                        padding: '10px',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px',
                                                        backgroundColor: '#f8f9fa',
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    <small className="text-muted">#{dbDomains.length + i + 1}</small>
                                                    <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>{d}</p>
                                                </div>
                                            ))}
                                            {sisterDomains.length === 0 && dbDomains.length === 0 && (
                                                <p className="text-muted">No domains found.</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer">
                                {!showSisterList ? (
                                    <button
                                        className={`${button.btn} ${button.btnBlue}`}
                                        onClick={handleAddSisterDomains}
                                        disabled={loading}
                                    >
                                        {loading ? 'Loading...' : 'Get Sister Domains'}
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            className={`${button.btn} ${button.btnBlue}`}
                                            onClick={handleDownloadSisterDomains}
                                        >
                                            <i className="fas fa-download"></i> Download
                                        </button>
                                        <button
                                            className={`${button.btn} ${button.btnBlue}`}
                                            onClick={handleCloseModal}
                                        >
                                            Close
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SiteExpansion;
